import UploadItem from "sap/m/upload/UploadItem";
import BaseController from "./BaseController";
import Table from "sap/m/Table";
import {
  AttachmentDto,
  AttachmentSelectionDto,
  AttachmentUploadResponse,
} from "../../models/AttachmentDto";
import { GenerateGUID } from "../../utils/GeneratorUtil";
import Item from "sap/ui/core/Item";
import { GetApiAccessToken } from "../../services/AuthService";
import {
  ItemInfo,
  UploadSetwithTable$BeforeUploadStartsEvent,
  UploadSetwithTable$UploadCompletedEvent,
} from "sap/m/plugins/UploadSetwithTable";
import { IndicationColor, ValueState } from "sap/ui/core/library";
import MessageType from "sap/ui/core/message/MessageType";
import { AppSettings } from "../../constants/AppSettings";
import Dialog from "sap/m/Dialog";
import Fragment from "sap/ui/core/Fragment";
import MultiInput from "sap/m/MultiInput";
import JSONModel from "sap/ui/model/json/JSONModel";
import Token from "sap/m/Token";
import { WorkOrderDetailsInternalDto } from "akademika/akamont/models/WorkOrderDetailsInternalDto";
import ManagedAttachmentUtil from "akademika/akamont/utils/ManagedAttachmentUtil";
import { FormatAttachmentInfo } from "akademika/akamont/utils/FormatUtil";

export class AttachmentController extends BaseController {
  protected requestZipDownloadUri = "api/WorkOrder/RequestZipDownload";
  protected downloadZippedAttachmentsUri = "api/WorkOrder/DownloadZippedAttachments?token={downloadToken}";
  protected uploadAttachmentUri = "api/WorkOrder/UploadAttachment";
  protected mailAttachmentsUri = "api/WorkOrder/MailAttachments";
  protected downloadWorkOrderAttachmentUri =
    "api/WorkOrder/DownloadWorkOrderAttachment/{attachmentId}";
  protected downloadWorkOrderItemAttachmentUri =
    "api/WorkOrder/DownloadWorkOrderItemAttachment/{attachmentId}";

  protected _attachmentsMailAndDownloadDialog: Dialog | null;
  private _attachmentsMailAndDownloadFragmentName = "akademika.akamont.pages.sharedDialogs.MailOrDownloadAttachmentsDialog";

  // Used to queue waiting uploads from the UploadSetwithTablePlugin
  protected _waitingForUploadQueue: {
    item: UploadItem;
    resolve: (value: unknown) => void;
    reject: unknown;
  }[] = [];
  // When upload is triggered promises are stored in _uploadingQueue, so we can wait until uploads are finished
  protected _pendingUploadsQueue: Promise<unknown>[] = [];
  protected _pendingUploadsCallbacks: {
    item: UploadItem;
    resolve: (value: unknown) => void;
    reject: (value: unknown) => void;
  }[] = []; // test

  private getBindingPath(source: Table) {
    let bindingContext = source.getBindingContext()?.getPath() ?? "";
    let propertyPath = source.getBinding("items")?.getPath()?.replace("/", "");
    return `${bindingContext}/${propertyPath}`;
  }

  public manageAttachmentFrontendState(
    item: UploadItem,
    bindingPath: string,
    modelName: string | undefined = undefined
  ) {
    const allowedTypes = AppSettings.files.allowedTypes;
    const fileType = item.getMediaType();

    if (!allowedTypes.includes(fileType)) {
      this.openSimpleDialog({
        title: this.translate("fileTypeNotAllowedTitle"),
        message: `${this.translate("fileTypeNotAllowed")} ${fileType}`,
        state: ValueState.Error,
        confirmWithComment: false,
      });
      return null;
    }

    const attachment: AttachmentDto = {
      name: item.getFileName(),
      size: item.getFileSize(),
      mimetype: item.getMediaType(),
      uploadState: item.getUploadState(),
      uploadId: GenerateGUID(),
    };

    const attachments = this.getModel(modelName).getProperty(
      bindingPath
    ) as AttachmentDto[];
    this.getModel().setProperty(bindingPath, [...attachments, attachment]);
    return attachment;
  }

  private setUploadHeaders(item: UploadItem, uploadId?: string) {
    item.addHeaderField(
      new Item({
        key: "uploadId",
        text: uploadId,
      })
    );

    item.addHeaderField(
      new Item({
        key: "Authorization",
        text: "Bearer " + GetApiAccessToken(),
      })
    );
  }

  public onFileSizeTooBig() {
    this.openSimpleDialog({
      title: this.translate("fileSizeExceededTitle"),
      message: `${this.translate("fileSizeExceeded")} ${AppSettings.files.maxAllowedSize} MB`,
      state: ValueState.Error,
      confirmWithComment: false,
    });
  }

  public handleAttachmentValidation(
    itemInfo: ItemInfo,
    modelName: string | undefined = undefined
  ) {
    const { oItem, oSource } = itemInfo;
    const sourceTable = oSource.getParent() as Table;
    const bindingPath = this.getBindingPath(sourceTable);
    const attachment = this.manageAttachmentFrontendState(
      oItem,
      bindingPath,
      modelName
    );
    if (attachment) this.setUploadHeaders(oItem, attachment.uploadId);

    const itemPromise = new Promise((resolve, reject) => {
      this._waitingForUploadQueue.push({
        item: oItem,
        resolve: resolve,
        reject: reject,
      });
    });

    return itemPromise;
  }

  public triggerUploadAttachments() {
    while (this._waitingForUploadQueue.length > 0) {
      const task = this._waitingForUploadQueue.shift();
      if (task !== undefined) {
        const pendingPromise = new Promise((resolve, reject) => {
          const item = task?.item;
          this._pendingUploadsCallbacks.push({ item, resolve, reject });
        });

        this._pendingUploadsQueue.push(pendingPromise);
        task?.resolve(task.item);
      }
    }
  }

  public handleUploadCompleted(
    event: UploadSetwithTable$UploadCompletedEvent,
    modelName: string | undefined = undefined
  ) {
    const statusParameter = event.getParameter("status");
    const item = event.getParameter("item");
    const responseStatus =
      statusParameter !== undefined ? Number(statusParameter) : 0;
    const sourceTable = event.getSource().getParent() as Table;
    const bindingPath = this.getBindingPath(sourceTable);
    const callback = this._pendingUploadsCallbacks.find((i) => i.item == item);
    if (responseStatus === 200) {
      const responseData = JSON.parse(
        event.getParameter("response") as string
      ) as AttachmentUploadResponse;
      const attachments: AttachmentDto[] =
        this.getModel(modelName).getProperty(bindingPath);
      const attachment = attachments.find(
        (a) => a.uploadId == responseData.content.uploadId
      );
      if (attachment) {
        attachment.uploadState = item?.getUploadState();
        attachment.uploadId = responseData.content.uploadId;
      }
      this.getModel().refresh();
      callback?.resolve(responseStatus);
      return;
    }
    callback?.reject(null);
  }

  public handleBeforeUploadStarts(
    event: UploadSetwithTable$BeforeUploadStartsEvent
  ) {
    const item = event.getParameter("item") as UploadItem;
    const sourceTable = event.getSource().getParent() as Table;
    const bindingPath = this.getBindingPath(sourceTable);
    const attachment = this.manageAttachmentFrontendState(
      item,
      bindingPath,
      undefined
    );
    if (attachment) this.setUploadHeaders(item, attachment.uploadId);
  }

  public attachmentHighlightFormatter(value: string) {
    switch (value) {
      case "Ready":
        return IndicationColor.Indication05;
      case "Complete":
        return IndicationColor.Indication04;
      case "Error":
        return IndicationColor.Indication01;
      default:
        return null;
    }
  }

  public async openMailOrDownloadAttachments() {
    const attachments = this.getSelectedWorkOrderAttachments();

    const attachmentsModel = new JSONModel({ items: attachments });
    this._attachmentsMailAndDownloadDialog = await this.openDialog(
      this._attachmentsMailAndDownloadFragmentName,
      true
    );
    this.getView()?.addDependent(this._attachmentsMailAndDownloadDialog);
    this._attachmentsMailAndDownloadDialog.setModel(attachmentsModel, "attachmentsModel");

    this.initEmailMultiInput(`${this._attachmentsMailAndDownloadFragmentName}Dialog`);
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private initEmailMultiInput(fragmentId: string) {
    const multiInput = Fragment.byId(fragmentId, "emailRecipients") as MultiInput;

    const validatorFn = (args: { text?: string }) => {
      const text = args?.text;

      if (!text || text.trim() === "") {
        return null;
      }

      if (!this.isValidEmail(text)) {
        multiInput.setValueState(ValueState.Error);
        multiInput.setValueStateText(`${this.translate("invalidEmailFormat")}: ${text}`);

        return null;
      }

      multiInput.setValueState(ValueState.None);
      return new Token({ key: text, text: text });
    };

    multiInput.addValidator(validatorFn);
  }

  private getSelectedWorkOrderAttachments(): AttachmentSelectionDto[] {
    const workOrderDetails = this.getModelData<WorkOrderDetailsInternalDto>();
    const attachments: AttachmentSelectionDto[] = [];

    if (workOrderDetails.attachments) {
      for (const attachment of workOrderDetails.attachments) {
        attachments.push({
          ...attachment,
          groupName: this.translate("attachments"),
          sourceType: "WorkOrderAttachment",
        });
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const serviceOptions = (this.getModel("serviceOptions") as JSONModel)?.getData() as any[] ?? [];

    if (workOrderDetails.workOrderItems) {
      for (const item of workOrderDetails.workOrderItems) {
        if (item.attachments && item.attachments.length > 0) {

          const matchingOption = serviceOptions.find(
            (opt) => opt.serviceMeasurementUnitId === item.serviceMeasurementUnitId
          );

          let itemLabel: string;
          if (matchingOption) {
            const serviceText = this.translateDynamic(matchingOption.serviceNameTranslationKey);
            const unitText = this.translateDynamic(matchingOption.measurementUnitNameTranslationKey);
            itemLabel = `${serviceText} / ${unitText}`;
          } else {
            itemLabel = `${this.translate("item")} ${item.workOrderItemId || ""}`;
          }

          for (const attachment of item.attachments) {
            attachments.push({
              ...attachment,
              groupName: itemLabel,
              sourceType: "WorkOrderItemAttachment",
            });
          }
        }
      }
    }
    return attachments;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onDownloadSingleAttachment(oEvent: any) {
    const attachment = oEvent.getSource().getBindingContext("attachmentsModel").getObject() as AttachmentSelectionDto;
    if (!attachment.id) return;

    if (attachment.sourceType === "WorkOrderAttachment") {
      this.downloadWorkOrderAttachment(attachment.id);
    } else if (attachment.sourceType === "WorkOrderItemAttachment") {
      this.handleDialogAttachmentDownload(attachment.id);
    }
  }

  public async downloadWorkOrderAttachment(id: number | string) {
    const uri = this.downloadWorkOrderAttachmentUri.replace(
      "{attachmentId}",
      id.toString()
    );
    this.getApiService().downloadFile(uri);
  }

  public async handleDialogAttachmentDownload(id: number | string) {
    const uri = this.downloadWorkOrderItemAttachmentUri.replace(
      "{attachmentId}",
      id.toString()
    );
    this.getApiService().downloadFile(uri);
  }

  public areEmailRecipientsValid(emailRecipients: string[]): boolean {
    if (emailRecipients.length === 0) {
      this.openSimpleDialog({
        title: "emailRecipients",
        message: "atLeastOneEmailRequired",
        state: ValueState.Error,
        confirmWithComment: false,
      });
      return false;
    }
    const invalidEmails = emailRecipients.filter(email => !this.isValidEmail(email));
    if (invalidEmails.length > 0) {
      return false;
    }
    return true;
  }

  public async onDownloadSelectedAttachments() {
    const fragmentId = `${this._attachmentsMailAndDownloadFragmentName}Dialog`;
    const table = Fragment.byId(fragmentId, "attachmentsMailTable") as Table;
    const selectedContexts = table?.getSelectedContexts();

    const attachments = selectedContexts?.map(ctx => ctx.getObject() as AttachmentSelectionDto) ?? [];
    if (!this.areAttachmentsValid(attachments)) return;

    try {
      const response = await this.post<{ token: string }>(this.requestZipDownloadUri, {
        body: attachments
      });

      const downloadToken = response.token;
      const baseUrl = this.getApiService().getBaseUrl();

      const downloadUrl = `${baseUrl}${this.downloadZippedAttachmentsUri}`.replace("{downloadToken}", downloadToken);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = "_self";
      link.download = ""; // This tells the browser it's a file
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100)
    } catch (error) {
      console.error("Download Error:", error);
    }
  }

  private areAttachmentsValid(attachments: AttachmentSelectionDto[]): boolean {
    if (attachments.length === 0) {
      this.openSimpleDialog({
        title: "attachments",
        message: "noAttachmentsSelected",
        state: ValueState.Error,
        confirmWithComment: false,
      });
      return false;
    }

    return true
  }

  public onCloseMailOrDownloadDialog() {
    if (this._attachmentsMailAndDownloadDialog) {
      this._attachmentsMailAndDownloadDialog.close();
      this._attachmentsMailAndDownloadDialog.destroy();
      this._attachmentsMailAndDownloadDialog = null;
    }
  }

  public async onMailSelectedAttachments() {
    const fragmentId = `${this._attachmentsMailAndDownloadFragmentName}Dialog`;
    const table = Fragment.byId(fragmentId, "attachmentsMailTable") as Table;
    const selectedContexts = table?.getSelectedContexts();

    const attachments = selectedContexts?.map(ctx => ctx.getObject() as AttachmentSelectionDto) ?? [];
    if (!this.areAttachmentsValid(attachments)) return;

    const multiInput = Fragment.byId(fragmentId, "emailRecipients") as MultiInput;
    if (multiInput.getValueState() === ValueState.Error) {
      this.openSimpleDialog({
        title: "emailRecipients",
        message: multiInput.getValueStateText(),
        state: ValueState.Error,
        confirmWithComment: false,
      });
      return;
    }

    const tokens = multiInput.getTokens();
    const emailRecipients = tokens?.map(token => token.getText()) ?? [];
    if (!this.areEmailRecipientsValid(emailRecipients)) return;

    const workOrderId = this.routeParams.workOrderId;
    await this.post(this.mailAttachmentsUri, {
      body: {
        workOrderId,
        emailRecipients: emailRecipients,
        attachments: attachments,
      },
    }).then(() => {
      this.showMessage("mailSentSuccessfully", MessageType.Success, true, this._attachmentsMailAndDownloadFragmentName);
    });
  }

  public handleAttachmentPreview = async (attachment: AttachmentSelectionDto, sourceType: AttachmentSelectionDto["sourceType"]) => {
    if (sourceType) attachment.sourceType = sourceType;
    await new ManagedAttachmentUtil(this, attachment).handlePreview();
  }

  public handleAttachmentPreviewEnabled(mimetype: string) {
    return AppSettings.files.supportedPreviewTypes.includes(mimetype);
  }

  public formatAttachmentInfo = FormatAttachmentInfo;

  protected _destoryExistingDialogs() {
    if (this._oDialog) {
      this._oDialog.destroy();
    }
    if (this._attachmentsMailAndDownloadDialog) {
      this._attachmentsMailAndDownloadDialog.destroy();
    }
  }
}
